import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { Order, OrderStatus } from '@runninglime/shared';
import { apiGet, apiPut } from '../../lib/api.ts';
import { formatDate, formatPrice, statusColor } from '../../lib/utils.ts';
import { StatusBadge, LoadingSpinner } from '../../components/ui/Shared.tsx';

const ALL_STATUSES: OrderStatus[] = ['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'problem'];

export default function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    const res = await apiGet<Order>(`/admin/orders/${id}`);
    if (res.success && res.data) setOrder(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status: OrderStatus) => {
    setUpdating(true);
    await apiPut(`/admin/orders/${id}/status`, { status });
    await load();
    setUpdating(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!order) return <div className="text-center py-8 text-gray-500">Order not found</div>;

  return (
    <div className="animate-fade-in max-w-4xl">
      <Link to="/admin/orders" className="text-sm text-gray-500 hover:text-green transition mb-4 inline-flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Orders
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-dark">Order #{order.id.slice(0, 8)}</h1>
          <p className="text-gray-500 text-sm mt-1">Placed {formatDate(order.createdAt)}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Items */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-dark">Items</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {order.items.map((item, i) => (
              <div key={i} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-dark">{item.productName}</p>
                  <p className="text-sm text-gray-500">{item.variantSize} · Qty: {item.quantity}</p>
                </div>
                <p className="font-medium text-dark">{formatPrice(item.subtotal)}</p>
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
            <span className="font-semibold text-dark">Total</span>
            <span className="font-bold text-lg text-dark">{formatPrice(order.total)}</span>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Update */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Update Status</h3>
            <div className="grid grid-cols-2 gap-2">
              {ALL_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => changeStatus(s)}
                  disabled={updating || s === order.status}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition capitalize ${
                    s === order.status
                      ? 'bg-lime text-dark ring-2 ring-lime'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="glass rounded-2xl p-5">
            <h3 className="font-semibold text-dark mb-3">Details</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Customer</dt>
                <dd className="font-mono text-dark">{order.userId.slice(0, 12)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Items</dt>
                <dd className="text-dark">{order.items.length}</dd>
              </div>
              {order.note && (
                <div className="pt-2 border-t border-gray-100">
                  <dt className="text-gray-500 mb-1">Note</dt>
                  <dd className="text-dark text-sm">{order.note}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Status History */}
          {order.statusHistory && order.statusHistory.length > 0 && (
            <div className="glass rounded-2xl p-5">
              <h3 className="font-semibold text-dark mb-3">Status History</h3>
              <div className="space-y-2">
                {order.statusHistory.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${statusColor(h.status).includes('green') ? 'bg-green' : statusColor(h.status).includes('yellow') ? 'bg-yellow-500' : statusColor(h.status).includes('red') ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <span className="capitalize text-dark">{h.status}</span>
                    <span className="text-gray-400 ml-auto text-xs">{formatDate(h.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
